// Task links (D66): a task may sit under a parent of the same group (a Jira sub-task, or set by
// hand). The tasks table shows each child under its parent, behind an expand toggle, so the list is
// ordered here rather than by the table's column sort.
import type { Task } from "./model";

export interface TaskRow {
  task: Task;
  /** 0 for a top-level task, 1 for its children, and so on. */
  depth: number;
  /** Children of this task in the list (the API's `child_count` may include none listed). */
  children: number;
}

const newestFirst = (a: Task, b: Task) => b.updated_at - a.updated_at;

/**
 * The visible rows: top-level tasks newest first, each followed by its children (newest first)
 * when it is expanded. A task whose parent is not in the list is shown at the top level, and a
 * cycle (which the API refuses) cannot loop because each task is placed once.
 */
export function taskRows(tasks: Task[], expanded: ReadonlySet<string>): TaskRow[] {
  const ids = new Set(tasks.map((t) => t.id));
  const children = new Map<string, Task[]>();
  const roots: Task[] = [];
  for (const t of tasks) {
    const parent = t.parent_task_id;
    if (parent && parent !== t.id && ids.has(parent)) {
      children.set(parent, [...(children.get(parent) ?? []), t]);
    } else {
      roots.push(t);
    }
  }
  const rows: TaskRow[] = [];
  const placed = new Set<string>();
  const visit = (t: Task, depth: number) => {
    if (placed.has(t.id)) return;
    placed.add(t.id);
    const kids = (children.get(t.id) ?? []).sort(newestFirst);
    rows.push({ task: t, depth, children: kids.length });
    if (expanded.has(t.id)) for (const k of kids) visit(k, depth + 1);
  };
  // Tasks in a cycle have no root above them: the newest of each becomes one, so none is lost.
  const reached = new Set<string>();
  const reach = (id: string) => {
    if (reached.has(id)) return;
    reached.add(id);
    for (const k of children.get(id) ?? []) reach(k.id);
  };
  roots.forEach((t) => reach(t.id));
  for (const t of [...tasks].sort(newestFirst)) {
    if (reached.has(t.id)) continue;
    roots.push(t);
    reach(t.id);
  }
  for (const t of roots.sort(newestFirst)) visit(t, 0);
  return rows;
}

/** Tasks that may become `task`'s parent: the group's other tasks, minus its own descendants. */
export function parentCandidates(task: Task, tasks: Task[]): Task[] {
  const below = new Set<string>([task.id]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const t of tasks) {
      if (t.parent_task_id && below.has(t.parent_task_id) && !below.has(t.id)) {
        below.add(t.id);
        grew = true;
      }
    }
  }
  return tasks.filter((t) => !below.has(t.id));
}
